import * as BreadcrumbsClasses from '@/theme/Breadcrumbs.module.scss';
import * as MenuClasses from '@/theme/Menu.module.scss';
import * as ModalClasses from '@/theme/Modal.module.scss';
import * as NavLinkClasses from '@/theme/NavLink.module.scss';
import * as PaperClasses from '@/theme/Paper.module.scss';
import * as TableClasses from '@/theme/Table.module.scss';
import * as TabsClasses from '@/theme/Tabs.module.scss';

import { Breadcrumbs, createTheme, Menu, Modal, NavLink, Paper, Table, Tabs } from '@mantine/core';

const theme = createTheme({
  colorScheme: 'light',
  defaultColorScheme: 'light',

  fontFamily: '"Switzer", sans-serif',
  fontFamilyMonospace: '"DM Mono", monospace',
  fontSizes: {
    xs: '0.8rem', // 12px
    sm: '0.9rem', // 14px
    md: '1.0rem', // 15px
    lg: '1.1rem', // 18px
    xl: '1.2rem', // 20px
    xxl: '1.3rem', // 24px
  },
  lineHeights: {
    xs: 1.1,
    sm: 1.3,
    md: 1.5,
    lg: 1.7,
    xl: 1.8,
    xxl: 1.9,
  },
  headings: {
    fontFamily: '"Cabinet Grotesk", sans-serif',
    sizes: {
      h1: { fontSize: '1.6rem' },
      h2: { fontSize: '1.4rem' },
      h3: { fontSize: '1.25rem' },
      h4: { fontSize: '1.2rem' },
      h5: { fontSize: '1.1rem' },
      h6: { fontSize: '1rem' },
    },
    fontWeight: 800,
  },
  other: {
    cabinet: '"Cabinet Grotesk", sans-serif',
  },
  primaryColor: 'rose-rush',
  primaryShade: 6,
  components: {
    ActionIcon: {
      styles: {
        root: {
          cursor: 'default',
        },
      },
    },
    Button: {
      styles: {
        root: {
          fontFamily: '"Cabinet Grotesk", sans-serif',
          fontWeight: '800',
          cursor: 'default',
        },
      },
    },
    Input: {
      styles: {
        input: {
          '@media (max-width: 768px)': {
            fontSize: '16px',
          },
        },
      },
    },
    TextInput: {
      styles: {
        input: {
          '@media (max-width: 768px)': {
            fontSize: '16px',
          },
        },
      },
    },
    Select: {
      styles: {
        input: {
          '@media (max-width: 768px)': {
            fontSize: '16px',
          },
        },
      },
    },
    Tabs: Tabs.extend({
      classNames: {
        root: TabsClasses.root,
        list: TabsClasses.list,
        tab: TabsClasses.tab,
        tabLabel: TabsClasses.tabLabel,
      },
    }),
    NavLink: NavLink.extend({
      classNames: {
        root: NavLinkClasses.root,
        label: NavLinkClasses.label,
        description: NavLinkClasses.description,
      },
    }),
    Breadcrumbs: Breadcrumbs.extend({
      classNames: {
        separator: BreadcrumbsClasses.separator,
        breadcrumb: BreadcrumbsClasses.breadcrumb,
      },
    }),
    Modal: Modal.extend({
      classNames: {
        content: ModalClasses.content,
        header: ModalClasses.header,
        title: ModalClasses.title,
        body: ModalClasses.body,
        close: ModalClasses.close,
      },
    }),
    Table: Table.extend({
      classNames: {
        thead: TableClasses.thead,
        th: TableClasses.th,
      },
    }),
    Textarea: {
      styles: {
        input: {
          paddingTop: '0.3rem',
          paddingBottom: '0.3rem',
          '@media (max-width: 768px)': {
            fontSize: '16px',
          },
        },
      },
    },
    Menu: Menu.extend({
      classNames: {
        item: MenuClasses.item,
      },
      styles: {
        target: {
          '& button': {
            backgroundColor: 'var(--mantine-color-rose-rush-filled) !important',
            color: 'var(--mantine-color-white) !important',
            border: '1px solid #000 !important',
            borderBottom: '5px solid #000 !important',
            fontFamily: '"Cabinet Grotesk", sans-serif !important',
            fontWeight: '800 !important',
            '&:hover': {
              backgroundColor: 'var(--mantine-color-rose-rush-filled-hover) !important',
              borderBottom: '5px solid var(--mantine-color-rose-rush-7) !important',
            },
          },
        },
      },
    }),
    Paper: Paper.extend({
      classNames: {
        root: PaperClasses.root,
      },
    }),
  },

  colors: {
    'sail-maker': [
      '#ecf8fd',
      '#daeef6',
      '#b0dcef',
      '#84c9e9',
      '#63bae3',
      '#51b0e0',
      '#45abe0',
      '#3896c7',
      '#2b85b2',
      '#0b4f6c',
    ],
    'rose-rush': [
      '#fff6f9',
      '#fcd5df',
      '#f2a9bb',
      '#e97b96',
      '#e15476',
      '#dd3b62',
      '#db2955',
      '#c31f48',
      '#af1740',
      '#9a0736',
    ],
    'aqua-fresco': [
      '#e6fbfb',
      '#daf2f2',
      '#b9e1e1',
      '#94d1d0',
      '#76c3c2',
      '#62bab9',
      '#55b6b5',
      '#44a1a0',
      '#358e8d',
      '#1c7c7b',
    ],
    'wind-chime': [
      '#f4f4fa',
      '#e0e0e2',
      '#cccccc',
      '#afafb1',
      '#979799',
      '#88888b',
      '#808085',
      '#6e6e73',
      '#616168',
      '#53535e',
    ],
    'dark-grey': [
      '#f3f3f6',
      '#e4e4e6',
      '#c7c7cd',
      '#a8a7b4',
      '#8d8d9f',
      '#7c7c93',
      '#74738e',
      '#62627b',
      '#57576f',
      '#060608',
    ],
    slate: [
      '#e8eaed',
      '#bbc0c9',
      '#768292',
      '#49586e',
      '#32435c',
      '#1b2e4a',
      '#182943',
      '#16253b',
      '#132034',
      '#0e1725',
    ],
    green: [
      '#e4fef1',
      '#d3f7e4',
      '#acedca',
      '#80e2ad',
      '#69dc9e',
      '#44d386',
      '#34d17d',
      '#24b86b',
      '#16a45d',
      '#008e4d',
    ],
    blue: [
      '#ebfcff',
      '#d8f7fb',
      '#abeff9',
      '#7ee7f7',
      '#61e0f6',
      '#54dcf5',
      '#4bdaf5',
      '#3fc1da',
      '#2eacc3',
      '#026c7c',
    ],
    red: ['#ffeaf3', '#fdd4e2', '#f4a7c0', '#ec779e', '#e54f80', '#e2366e', '#d81e5b', '#c81954', '#b3104a', '#9e003f'],
    orange: [
      '#ffeee4',
      '#ffdbcd',
      '#ffb69b',
      '#ff7f50',
      '#fe6c37',
      '#fe5719',
      '#ff4b09',
      '#e43c00',
      '#cb3300',
      '#b22800',
    ],
    violet: [
      '#fcedf6',
      '#f4d8e9',
      '#ecacd3',
      '#e47ebc',
      '#dd59a8',
      '#d9429c',
      '#d83796',
      '#bf2a82',
      '#ab2374',
      '#5f0f40',
    ],
  },
});

export default theme;
